import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Single Property Digital Branding für Luxusmakler Schweiz",
  "description": "Exklusiver Makler-Service: Single Property Branding, individuelle Landingpages und Storytelling für Luxusimmobilien ab 2 Mio. CHF in der Schweiz. Ohne Fotoshooting, ohne Zeitverlust.",
  "url": `${siteUrl}/makler-service`,
  "provider": {
    "@type": "Organization",
    "name": "SPDB Studio",
    "url": siteUrl
  },
  "areaServed": {
    "@type": "Country",
    "name": "Schweiz"
  },
  "serviceType": "Single Property Digital Branding",
  "offers": {
    "@type": "Offer",
    "priceCurrency": "CHF",
    "priceSpecification": {
      "@type": "PriceSpecification",
      "minPrice": "3500",
      "priceCurrency": "CHF",
      "description": "Projekte beginnen ab CHF 3.500. Jedes Angebot ist individuell."
    }
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Was kostet das Single Property Branding?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Unsere Leistungen sind auf Anfrage erhältlich — der Preis richtet sich nach Umfang, Objektgröße und gewünschtem Sprachumfang. Als Orientierung: Projekte beginnen in der Regel bei CHF 3.500. Jede Anfrage wird individuell kalkuliert und unverbindlich angeboten."
      }
    },
    {
      "@type": "Question",
      "name": "Wie lange dauert die vollständige Umsetzung?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Das erste Branding-Konzept liefern wir innerhalb von 48 Stunden. Die vollständige Umsetzung inklusive Landingpage und Domain dauert in der Regel 5 bis 7 Werktage — abhängig vom Umfang und dem eingelieferten Bildmaterial."
      }
    },
    {
      "@type": "Question",
      "name": "Was muss ich als Makler liefern?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ausschließlich Ihr professionelles Bildmaterial — Architekturfotos, Drohnenaufnahmen, Interieurbilder. Alles weitere — Branding, Konzept, Text, Design und Technik — übernehmen wir vollständig."
      }
    },
    {
      "@type": "Question",
      "name": "Wie lange bleibt die Landingpage aktiv?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Die Domain und Seite bleiben aktiv, bis der Verkauf abgeschlossen ist. Auf Wunsch können wir die Seite auch darüber hinaus als Referenz bestehen lassen — etwa als Portfolio-Nachweis für Sie als Makler."
      }
    },
    {
      "@type": "Question",
      "name": "Ist die Seite auch in anderen Sprachen verfügbar?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja. Auf Anfrage erstellen wir mehrsprachige Versionen in Deutsch, Englisch und Französisch — ideal für internationale Käufer im Schweizer Luxussegment."
      }
    },
    {
      "@type": "Question",
      "name": "Für welche Objekte ist dieser Service geeignet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Wir arbeiten ausschließlich mit Objekten ab einem Marktwert von CHF 2 Millionen. Unterhalb dieser Schwelle kann unser Ansatz seinen vollen Mehrwert nicht entfalten — das ist unser Qualitätsanspruch, keine Arroganz."
      }
    }
  ]
};

const services = [
  {
    num: "I",
    title: "Bildmaterial-Sichtung & Strategie",
    desc: "Wir analysieren Ihr vorhandenes Profi-Foto- und Videomaterial — Drohnenaufnahmen, Architekturfotos, Interieurbilder. Gemeinsam definieren wir die erzählerische Linie, die zum Objekt passt.",
    detail: "Was Sie liefern: Ihr bestehendes Bildmaterial. Was wir zurückgeben: Eine klare Vision.",
    image: "https://static.readdy.ai/image/19164d6e6195eaf5ae2814b8eb9e837d/c1b520fa894c199f0cb85de16752bf4d.png",
  },
  {
    num: "II",
    title: "Single Property Branding",
    desc: "Jedes Objekt erhält eine einzigartige Identität: Eigenname, maßgeschneiderte Typografie, eine subtile Farbwelt, ein Logo-Element und eine Bildsprache — alles kohärent, alles unvergesslich.",
    detail: "Kein Template, keine Wiederholung. Jede Marke ist ein Unikat.",
    image: "https://static.readdy.ai/image/19164d6e6195eaf5ae2814b8eb9e837d/70bb249ece0731bddc67ce843f1371aa.png",
  },
  {
    num: "III",
    title: "Exklusive Web-Erfahrung",
    desc: "Das Herzstück: Eine maßgeschneiderte Landingpage mit eigenem Domain-Namen. Vollbild-Galerien, Storytelling-Elemente, animierte Übergänge — entworfen wie eine Kunstinstallation, gebaut wie eine Technologie.",
    detail: "Eigene Domain. Eigenes Erlebnis. Eigene Sprache.",
    image: "https://static.readdy.ai/image/19164d6e6195eaf5ae2814b8eb9e837d/7d227e00f015409910f2c086c4070a45.png",
  },
];

const benefits = [
  { icon: "ri-award-line", title: "Prestige-Wahrnehmung", desc: "Käufer erleben Ihr Objekt als Marke — nicht als Anzeige." },
  { icon: "ri-time-line", title: "Verkaufsbeschleunigung", desc: "Ein emotionales Erlebnis kürzt Entscheidungswege radikal." },
  { icon: "ri-shield-check-line", title: "Diskretion garantiert", desc: "Kein öffentliches Portal. Nur Ihre Käufer, nur Ihr Objekt." },
  { icon: "ri-global-line", title: "Internationale Reichweite", desc: "Mehrsprachige Versionen auf Anfrage — für globale Klientel." },
];

const faqs = [
  {
    q: "Was kostet das Single Property Branding?",
    a: "Unsere Leistungen sind auf Anfrage erhältlich — der Preis richtet sich nach Umfang, Objektgröße und gewünschtem Sprachumfang. Als Orientierung: Projekte beginnen in der Regel bei CHF 3.500. Jede Anfrage wird individuell kalkuliert und unverbindlich angeboten.",
  },
  {
    q: "Wie lange dauert die vollständige Umsetzung?",
    a: "Das erste Branding-Konzept liefern wir innerhalb von 48 Stunden. Die vollständige Umsetzung inklusive Landingpage und Domain dauert in der Regel 5 bis 7 Werktage — abhängig vom Umfang und dem eingelieferten Bildmaterial.",
  },
  {
    q: "Was muss ich als Makler liefern?",
    a: "Ausschließlich Ihr professionelles Bildmaterial — Architekturfotos, Drohnenaufnahmen, Interieurbilder. Alles weitere — Branding, Konzept, Text, Design und Technik — übernehmen wir vollständig.",
  },
  {
    q: "Wie lange bleibt die Landingpage aktiv?",
    a: "Die Domain und Seite bleiben aktiv, bis der Verkauf abgeschlossen ist. Auf Wunsch können wir die Seite auch darüber hinaus als Referenz bestehen lassen — etwa als Portfolio-Nachweis für Sie als Makler.",
  },
  {
    q: "Ist die Seite auch in anderen Sprachen verfügbar?",
    a: "Ja. Auf Anfrage erstellen wir mehrsprachige Versionen in Deutsch, Englisch und Französisch — ideal für internationale Käufer im Schweizer Luxussegment.",
  },
  {
    q: "Für welche Objekte ist dieser Service geeignet?",
    a: "Wir arbeiten ausschließlich mit Objekten ab einem Marktwert von CHF 2 Millionen. Unterhalb dieser Schwelle kann unser Ansatz seinen vollen Mehrwert nicht entfalten — das ist unser Qualitätsanspruch, keine Arroganz.",
  },
];

function ServiceBlock({ service, index }: { service: typeof services[0]; index: number }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center py-20 border-b border-light-gray last:border-0 transition-all duration-800 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {/* Image */}
      <div className={`${isEven ? "order-1 lg:order-1" : "order-1 lg:order-2"}`}>
        <div className="relative overflow-hidden" style={{ aspectRatio: "7/5" }}>
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute top-6 left-6 font-serif text-7xl text-white/15 font-semibold select-none">
            {service.num}
          </div>
        </div>
      </div>

      {/* Text */}
      <div className={`${isEven ? "order-2 lg:order-2" : "order-2 lg:order-1"}`}>
        <span className="font-serif text-6xl text-gold/15 font-semibold">{service.num}</span>
        <h3 className="font-serif text-3xl lg:text-4xl text-text-primary font-medium mt-2 mb-5 leading-tight">
          {service.title}
        </h3>
        <span className="gold-divider-left mb-7" />
        <p className="font-sans font-light text-text-secondary text-base leading-relaxed mb-6">
          {service.desc}
        </p>
        <p className="font-sans text-sm text-gold font-medium italic">{service.detail}</p>
      </div>
    </div>
  );
}

function FaqItem({ faq, index }: { faq: typeof faqs[0]; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-light-gray last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between py-7 text-left gap-8 cursor-pointer group"
      >
        <span className="font-sans font-medium text-text-primary text-sm leading-relaxed group-hover:text-gold transition-colors duration-300">
          {faq.q}
        </span>
        <span className={`w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5 transition-transform duration-300 ${open ? "rotate-45" : ""}`}>
          <i className="ri-add-line text-gold text-lg" />
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-400 ${
          open ? "max-h-64 pb-7" : "max-h-0"
        }`}
      >
        <p className="font-sans font-light text-text-secondary text-sm leading-relaxed">
          {faq.a}
        </p>
      </div>
    </div>
  );
}

export default function MaklerServicePage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal(0.1);
  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollReveal(0.1);
  const { ref: faqRef, isVisible: faqVisible } = useScrollReveal(0.1);

  return (
    <>
      <title>Makler Service – Single Property Branding Schweiz | SPDB Studio</title>
      <meta name="description" content="Exklusiver Makler-Service: Single Property Branding, Landingpages und digitales Storytelling für Luxusimmobilien ab 2 Mio. CHF. Ohne Fotoshooting. In 5–7 Werktagen. SPDB Studio Zürich." />
      <meta name="keywords" content="Makler Service Zürich, Single Property Branding Schweiz, Luxusimmobilien Landingpage, Immobilien Webdesign Makler" />
      <link rel="canonical" href={`${siteUrl}/makler-service`} />
      <JsonLd data={serviceSchema} />
      <JsonLd data={faqSchema} />
      <main className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <section className="pt-48 pb-24 px-8 lg:px-16 bg-warm-white">
          <div
            ref={heroRef}
            className={`max-w-3xl mx-auto text-center transition-all duration-800 ${
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="section-label mb-6">Für Luxusmakler</p>
            <h1 className="font-serif text-5xl md:text-6xl text-text-primary font-medium leading-tight mb-6">
              Ihr Mehrwert.
              <br />
              <em className="italic text-gold">Messbar. Sichtbar.</em>
            </h1>
            <span className="gold-divider mb-8" />
            <p className="font-sans font-light text-text-secondary text-base leading-relaxed max-w-xl mx-auto">
              Wir nehmen Ihr vorhandenes Bildmaterial und schaffen daraus eine digitale Markenwelt, die Ihr Objekt in eine eigene Liga hebt. Ohne Fotoshooting. Ohne Zeitverlust.
            </p>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 px-8 lg:px-16">
          <div className="max-w-screen-xl mx-auto">
            {services.map((service, index) => (
              <ServiceBlock key={service.num} service={service} index={index} />
            ))}
          </div>
        </section>

        {/* Benefits — complete redesign: light, editorial, luxury */}
        <section className="py-32 px-8 lg:px-16 relative overflow-hidden bg-white">
          {/* Subtle background texture */}
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="https://public.readdy.ai/ai/img_res/4234e871981c50c8c486b5ddeaff60b4.jpg"
              alt=""
              className="w-full h-full object-cover object-center opacity-[0.04]"
            />
          </div>

          <div className="max-w-screen-xl mx-auto relative z-10">
            {/* Section header — split layout */}
            <div
              ref={benefitsRef}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-end mb-24 transition-all duration-800 ${
                benefitsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div>
                <p className="section-label mb-6">Was Sie gewinnen</p>
                <h2 className="font-serif text-4xl md:text-5xl text-text-primary font-medium leading-tight">
                  Der Unterschied,
                  <br />
                  <em className="italic text-gold">den Käufer spüren.</em>
                </h2>
              </div>
              <div className="lg:pb-2">
                <span className="gold-divider-left mb-7" />
                <p className="font-sans font-light text-text-secondary text-base leading-relaxed">
                  Wir liefern keine Dienstleistung — wir liefern eine Erfahrung. Für Ihre Käufer. Und für Sie als Makler, der ab sofort anders wahrgenommen wird.
                </p>
              </div>
            </div>

            {/* Benefits grid — open, no dark cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-light-gray border border-light-gray">
              {benefits.map((b, i) => (
                <div
                  key={b.title}
                  className={`p-10 flex flex-col gap-0 transition-all duration-800 ${
                    benefitsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                  }`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  {/* Large faint number */}
                  <span className="font-serif text-7xl text-gold/10 font-semibold leading-none select-none mb-6">
                    0{i + 1}
                  </span>
                  {/* Icon */}
                  <div className="mb-6">
                    <i className={`${b.icon} text-gold text-3xl`} />
                  </div>
                  {/* Title */}
                  <h4 className="font-serif text-xl text-text-primary font-medium mb-3 leading-snug">
                    {b.title}
                  </h4>
                  {/* Gold micro-line */}
                  <div className="w-8 h-px bg-gold/40 mb-4" />
                  {/* Desc */}
                  <p className="font-sans font-light text-text-secondary text-sm leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-32 px-8 lg:px-16 bg-white">
          <div className="max-w-screen-xl mx-auto">
            <div
              ref={faqRef}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-20 transition-all duration-800 ${
                faqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div>
                <p className="section-label mb-6">Häufige Fragen</p>
                <h2 className="font-serif text-4xl lg:text-5xl text-text-primary font-medium leading-tight mb-6">
                  Was Sie wissen wollen.
                </h2>
                <span className="gold-divider-left mb-8" />
                <p className="font-sans font-light text-text-secondary text-sm leading-relaxed mb-8">
                  Wir glauben an Transparenz. Hier beantworten wir die häufigsten Fragen zu unserem Prozess, Preisen und Möglichkeiten.
                </p>
                {/* Preis-Hinweis */}
                <div className="border border-gold/30 p-7 bg-warm-white">
                  <p className="text-xs tracking-luxury font-sans text-text-muted uppercase mb-3">Investitionsrahmen</p>
                  <p className="font-serif text-2xl text-text-primary font-medium mb-2">Preis auf Anfrage</p>
                  <p className="font-sans font-light text-text-secondary text-sm leading-relaxed">
                    Projekte beginnen ab CHF 3.500. Jedes Angebot ist individuell — transparent, unverbindlich, diskret.
                  </p>
                  <Link to="/kontakt" className="inline-flex items-center gap-2 mt-5 text-xs tracking-luxury font-sans text-gold uppercase hover:opacity-70 transition-opacity duration-300">
                    <i className="ri-arrow-right-line" />
                    Angebot anfragen
                  </Link>
                </div>
              </div>

              <div className="flex flex-col justify-center">
                {faqs.map((faq, index) => (
                  <FaqItem key={index} faq={faq} index={index} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 px-8 lg:px-16 text-center bg-warm-white">
          <div className="max-w-xl mx-auto">
            <p className="section-label mb-6">Bereit für das nächste Level?</p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-primary font-medium leading-tight mb-6">
              Lassen Sie uns über Ihr Objekt sprechen.
            </h2>
            <span className="gold-divider mb-10" />
            <p className="font-sans font-light text-text-secondary text-base leading-relaxed mb-10">
              Jede Anfrage behandeln wir mit absoluter Diskretion. Kein Verkaufsgespräch — nur ein Gespräch unter Profis.
            </p>
            <Link to="/kontakt" className="btn-gold-fill">
              Diskrete Anfrage stellen
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
